import { Utils } from '@pacem/pacem-core';
import { isMesh, isCamera, isLight, isMeshGeometry } from '../../types';
import { KnownShader, isLineMaterial } from '../../materials/material';
import { WGSLBindingType } from './abstractions';
import { LABEL_PREFIX, MATERIAL_GROUP_INDEX, UNIFORM_GROUP_INDEX_LIGHTING, UNIFORM_GROUP_INDEX_CAMERA, UNIFORM_BINDING_INDEX_CAMERA, UNIFORM_GROUP_INDEX_TRANSFORM, UNIFORM_BINDING_INDEX_TRANSFORM, UNIFORM_GROUP_INDEX_INTERACTION, UNIFORM_BINDING_INDEX_INTERACTION, STORAGE_MATERIAL_INDEX, STORAGE_GROUP_INDEX_LINE, SAMPLER_BINDING_INDEX, TEXTURE_BINDING_INDEX, VERTEX_BUFFER_INDEX_MESH_POSITION, VERTEX_BUFFER_INDEX_MESH_NORMAL, VERTEX_BUFFER_INDEX_MESH_UV, VERTEX_COLOR_REFERENCE, LIGHTING_COLOR_REFERENCE, AMBIENT_COLOR_REFERENCE, UNIFORM_GROUP_INDEX_COLORPICKING, GPUShaderStage } from './constants';
import { BufferLayouts } from './buffers';
import { BasicMaterial, LineMaterial } from './materials';
import { getMeshVertexWgsl } from './wgsl/mesh-vertex';
import { getDefaultLineVertexWgsl, getFatLineVertexWgsl } from './wgsl/line-vertex';
import { getColorPickingFragmentWgsl } from './wgsl/object-picking-fragment';
import { textureMap } from './wgsl/texture-fragment';
import { directionalLight } from './wgsl/directional-light-fragment';
import { omniLight } from './wgsl/omni-light-fragment';
import { ambientLight } from './wgsl/ambient-light-fragment';
import { RenderableBuffers } from './renderablebuffer';
//namespace Pacem.Drawing3D.WebGPU {
// #region constants
const labelPrefix = LABEL_PREFIX;
const DefaultPipelineOptions = {
    multisample: true,
    packedVertices: true,
    primitive: {
        topology: 'triangle-list',
        frontFace: 'ccw',
        cullMode: 'back'
    }
};
const EMPTY_BIND_DELEGATE = (_) => { };
export class RenderPipelines {
    static { this._memoizer = new WeakMap(); }
    static dispose(ctx) {
        const memoizer = this._memoizer;
        const cache = memoizer.get(ctx);
        if (!Utils.isNull(cache)) {
            cache.clear();
            memoizer.delete(ctx);
        }
    }
    static createColorPicking(ctx, items) {
        const renderPipeline = RenderPipelines.create(ctx, items);
        if (renderPipeline instanceof MeshRenderPipelineClass
            || renderPipeline instanceof LineRenderPipelineClass) {
            return {
                frame: renderPipeline.raycast
            };
        }
        return null;
    }
    static create(ctx, [mesh, camera, ...lights], pipelineOptions) {
        const memoizer = RenderPipelines._memoizer;
        let cache = memoizer.get(ctx);
        if (Utils.isNull(cache)) {
            memoizer.set(ctx, cache = new Map());
        }
        //const camera = items.find(i => isCamera(i.item));
        //const lights = items.filter(i => isLight(i.item));
        //// find main object
        //const mesh = items.find(i => isMesh(i.item));
        if (!Utils.isNull(mesh)) {
            if (isMesh(mesh.item)) {
                const isMesh = isMeshGeometry(mesh.item.geometry);
                const isLine = !isMesh && isLineMaterial(mesh.item.material);
                if (!isMesh && !isLine) {
                    throw new Error('Pipeline not implemented, given the provided mesh argument.');
                }
                const builder = isMesh ? new MeshWGSLBuilder(mesh, camera, ...lights)
                    : new LineWGSLBuilder(mesh, camera);
                // cache peek for sudden return
                const key = builder.key + (pipelineOptions?.modifiers?.version ?? '');
                const found = cache.get(key);
                if (found) {
                    return found;
                }
                const pipeline = isMesh ? new MeshRenderPipelineClass(ctx, builder)
                    : new LineRenderPipelineClass(ctx, builder);
                // no cache -> build it
                pipeline.build(pipelineOptions);
                const retval = pipeline;
                Utils.extend(retval, {
                    id: key + '-' + ctx.canvas.width + 'x' + ctx.canvas.height + '-' + Utils.Dates.now().toISOString()
                });
                cache.set(key, retval);
                return retval;
            }
            else {
                throw new ReferenceError('Pipeline not implemented, given thst the first argument is not a mesh.');
            }
        }
        else {
            throw new Error('Pipeline not implemented, given the provided arguments.');
        }
    }
}
class RenderPipelineCache {
    constructor() {
        this.#cache = new Map();
    }
    #cache;
    ensureBindGroup({ device }, item, buffers, refresh, layout, label, callback = () => { }) {
        const cache = this.#cache;
        const key = typeof item === 'string' ? item : item.key;
        let set = cache.get(key);
        if (Utils.isNull(set)) {
            cache.set(key, set = new Map());
        }
        if (refresh || !set.has(label)) {
            const entries = buffers.map((item, index) => {
                const entry = {
                    binding: index,
                    resource: {
                        buffer: item
                    }
                };
                return entry;
            });
            const descriptor = {
                label,
                layout,
                entries
            };
            // custom config happens here
            callback(descriptor);
            const retval = device.createBindGroup(descriptor);
            set.set(label, retval);
            return retval;
        }
        else {
            return set.get(label);
        }
    }
}
class MeshWGSLBuilder {
    #lights = [];
    #mesh;
    #camera;
    #bindgroups = new Map();
    #key = { shader: 'basic', lights: '' };
    constructor(...objects) {
        this.add(...objects);
    }
    reset() {
        this.#lights.splice(0);
        this.#mesh = null;
        this.#bindgroups.clear();
        this.#key = { shader: 'basic', lights: '' };
    }
    add(...objects) {
        for (let obj of objects ?? []) {
            if (isMesh(obj.item)) {
                if (Utils.isNull(this.#mesh)) {
                    this.#mesh = obj;
                    const material = obj.item.material ?? { shader: KnownShader.Basic };
                    this.#key.shader = material.shader + '-' + (material.map ? 'map' : 'nomap');
                }
                else {
                    throw new Error('Mesh already added.');
                }
            }
            else if (isLight(obj.item)) {
                const lights = this.#lights;
                lights.push(obj);
                this.#key.lights += ('-' + obj.item.type);
            }
            else if (isCamera(obj.item)) {
                this.#camera ??= obj;
            }
            else {
                throw new Error('Unknown object type.');
            }
        }
        // add interaction
    }
    build(modifiers) {
        const data = this._computeBasicData();
        const key = this.key, vertex = this.vertex({ inputModifier: modifiers?.vertexInput, outputModifier: modifiers?.vertexOutput }), fragment = this.fragment(data, { modifier: modifiers?.fragment }), bindings = (...args) => MeshWGSLBuilder.prototype.bindgroup.apply(this, args);
        const metadata = {
            lightCount: this.#lights.length,
            mapCount: this.#mesh.item.material?.map ? 1 : 0
        };
        return {
            vertex,
            fragment,
            key,
            bindings,
            metadata
        };
    }
    get key() {
        const { shader, lights } = this.#key;
        // TODO: consider backMap
        return `mesh-` + shader + lights;
        //return `mesh-${shader}${(hasTexture ? '-map' : '-nomap')}`;
    }
    bindgroup(type, key, index) {
        const set = this.#bindgroups;
        const arr = set.get(type);
        if (Utils.isNull(arr)) {
            return null;
        }
        if (Utils.isNullOrEmpty(key)) {
            return arr;
        }
        const filteredArr = arr.filter(i => i.key === key);
        if (isNaN(index)) {
            return filteredArr;
        }
        if (index >= 0 && index < arr.length) {
            return filteredArr[index];
        }
        return filteredArr;
    }
    vertex(options) {
        return getMeshVertexWgsl(options);
    }
    _computeBasicData() {
        const mesh = this.#mesh.item, lights = this.#lights, material = mesh.material, hasTexture = !Utils.isNull(material?.texture), hasLighting = !Utils.isNullOrEmpty(lights);
        const shader = Utils.isNull(mesh) ? KnownShader.Basic : (mesh.material?.shader ?? KnownShader.Basic);
        const materialBindGroupIndex = MATERIAL_GROUP_INDEX, lightingBindGroupIndex = UNIFORM_GROUP_INDEX_LIGHTING;
        return {
            hasTexture, hasLighting, shader,
            materialBindGroupIndex,
            lightingBindGroupIndex,
        };
    }
    _computeBindGroupsAndReferences(data = this._computeBasicData()) {
        const { hasTexture, materialBindGroupIndex, lightingBindGroupIndex } = data;
        const bindgroups = this.#bindgroups, lights = this.#lights;
        // build bindings
        // mesh + material
        const worldTransform = { key: 'objectWorldTransform', group: UNIFORM_GROUP_INDEX_TRANSFORM, binding: UNIFORM_BINDING_INDEX_TRANSFORM, type: 'buffer' };
        const material = { key: 'material', group: materialBindGroupIndex, binding: STORAGE_MATERIAL_INDEX, type: 'buffer' };
        if (hasTexture) {
            bindgroups.set(WGSLBindingType.Mesh, [worldTransform, material, { key: 'texture', group: materialBindGroupIndex, type: 'texture' }]);
        }
        else {
            bindgroups.set(WGSLBindingType.Mesh, [worldTransform, material]);
        }
        // camera
        bindgroups.set(WGSLBindingType.Camera, [
            { key: 'cameraViewTransform', group: UNIFORM_GROUP_INDEX_CAMERA, binding: UNIFORM_BINDING_INDEX_CAMERA, type: 'buffer' }
        ]);
        // lights
        const lArr = lights.map((_, ndx) => {
            return { key: 'light', group: lightingBindGroupIndex, binding: ndx, type: 'buffer' };
        });
        lArr.push(...lArr);
        // interaction
        bindgroups.set(WGSLBindingType.Interaction, [{ key: 'interaction', group: UNIFORM_GROUP_INDEX_INTERACTION, type: 'buffer', binding: UNIFORM_BINDING_INDEX_INTERACTION }]);
    }
    fragment(data = this._computeBasicData(), options) {
        this._computeBindGroupsAndReferences(data);
        const extra = {
            definitions: [], bindings: [], modifiers: []
        };
        const merge = (x) => {
            for (let definition of x.definitions ?? []) {
                if (extra.definitions.indexOf(definition) === -1) {
                    extra.definitions.push(definition);
                }
            }
            if (!Utils.isNullOrEmpty(x.bindings)) {
                extra.bindings.push(...x.bindings);
            }
            if (!Utils.isNullOrEmpty(x.modifiers)) {
                extra.modifiers.push(...x.modifiers);
            }
        };
        /* TODO: consider injecting customizations (options.modifier) into `WGSLFragmentExtra`*/
        switch (data.shader) {
            case KnownShader.Basic:
                // texture
                if (data.hasTexture) {
                    const materialGroupIndex = data.materialBindGroupIndex;
                    const wgslTex = textureMap({ inputColor: VERTEX_COLOR_REFERENCE, outputColors: [VERTEX_COLOR_REFERENCE], uv: 'uv' }, materialGroupIndex);
                    merge(wgslTex);
                }
                // lights
                let lightIndex = 0;
                const lights = this.#lights;
                for (let lightBuffer of lights) {
                    const light = lightBuffer.item; // TODO: consider implementing Pacem.Drawing3D.WebGPU.Light interface (as of Materials)
                    switch (light.type) {
                        case 'direction':
                            const dirLightExtra = directionalLight({ outputColor: LIGHTING_COLOR_REFERENCE, normal: 'normal' }, data.lightingBindGroupIndex, lightIndex);
                            merge(dirLightExtra);
                            break;
                        case 'omni':
                            const omniLightExtra = omniLight({ outputColor: LIGHTING_COLOR_REFERENCE, normal: 'normal', position: 'vertex', /*specularColor: MATERIAL_REFERENCE + '.specularColor', shininess: MATERIAL_REFERENCE + '.shininess'*/ }, data.lightingBindGroupIndex, lightIndex);
                            merge(omniLightExtra);
                            break;
                        case 'ambient':
                            const ambLightExtra = ambientLight({ inputColor: VERTEX_COLOR_REFERENCE, outputColor: AMBIENT_COLOR_REFERENCE }, data.lightingBindGroupIndex, lightIndex);
                            merge(ambLightExtra);
                            break;
                    }
                    lightIndex++;
                }
                return BasicMaterial.fragment(extra, options);
        }
    }
}
const PipelineUtils = {
    writeBufferConditionally: (device, buffer) => {
        if (!buffer.fresh) {
            return;
        }
        device.queue.writeBuffer(buffer.buffer, 0, buffer.value);
    }
};
class MeshRenderPipelineClass {
    constructor(_context, _builder, _cache = new RenderPipelineCache()) {
        this._context = _context;
        this._builder = _builder;
        this._cache = _cache;
        // #region fields and utils
        this._ensureU32idBindGroup = (item, buffer, refresh) => {
            return this._cache.ensureBindGroup(this._context, item, [buffer], refresh, this._pickBindGroupLayout, labelPrefix + 'mesh-colorpick-bind-group');
        };
        this._ensureMaterialBindGroup = (item, buffer, texture, refresh, layoutIndex = MATERIAL_GROUP_INDEX) => {
            const layout = this._bindGroupLayouts[layoutIndex], label = labelPrefix + 'material-bind-group';
            if (layout.label !== (labelPrefix + 'material-bind-group-layout')) {
                throw new Error('BindGroupLayout mismatch!');
            }
            return this._cache.ensureBindGroup(this._context, item, [buffer], refresh, layout, label, (descriptor) => {
                const { device } = this.context;
                const entries = descriptor.entries /* entries are build as array in the method */;
                if (texture) {
                    entries.push({
                        binding: SAMPLER_BINDING_INDEX, resource: device.createSampler({
                            addressModeU: 'repeat',
                            addressModeV: 'repeat',
                            magFilter: 'linear',
                            minFilter: 'linear',
                        })
                    }, {
                        binding: TEXTURE_BINDING_INDEX, resource: texture.createView()
                    });
                }
            });
        };
        this._ensureWorldTransformBindGroup = (item, buffer, refresh, layoutIndex = UNIFORM_GROUP_INDEX_TRANSFORM, raycasting = false) => {
            const layout = this._bindGroupLayouts[layoutIndex];
            if (layout.label !== (labelPrefix + 'mesh-transforms-bind-group-layout')) {
                throw new Error('BindGroupLayout mismatch!');
            }
            const key = item.key + '-' + (raycasting ? 1 : 0);
            const label = `${labelPrefix}${raycasting ? 'colorpick' : 'mesh'}-transforms-bind-group`;
            return this._cache.ensureBindGroup(this._context, key, [buffer], refresh, layout, label);
        };
        this._ensureCameraProjectionAndInteractionDataBindGroup = (item, viewBuffer, interactionBuffer, refresh, layoutIndex = UNIFORM_GROUP_INDEX_CAMERA, raycasting = false) => {
            const layout = this._bindGroupLayouts[layoutIndex];
            const key = item.key + '-' + (raycasting ? 1 : 0);
            const label = `${labelPrefix}${raycasting ? 'colorpick' : 'mesh'}-view-bind-group`;
            return this._cache.ensureBindGroup(this._context, key, [viewBuffer, interactionBuffer], refresh, layout, label);
        };
        this._ensureLightingBindGroup = (lights, refresh, layoutIndex = UNIFORM_GROUP_INDEX_LIGHTING) => {
            const layout = this._bindGroupLayouts[layoutIndex];
            const key = lights.map(l => l.id).join(':');
            const buffers = lights.map(l => l.buffer.buffer);
            return this._cache.ensureBindGroup(this._context, key, buffers, refresh, layout, labelPrefix + 'mesh-lighting-bind-group');
        };
        this._bindGroupLayouts = [];
        this._bindVertex = EMPTY_BIND_DELEGATE;
        this._bindPicker = EMPTY_BIND_DELEGATE;
        this._bindWorldTransform = EMPTY_BIND_DELEGATE;
        this._bindRaycastWorldTransform = EMPTY_BIND_DELEGATE;
        this._bindCameraProjectionAndInteractionData = EMPTY_BIND_DELEGATE;
        this._bindRaycastCameraProjectionAndInteractionData = EMPTY_BIND_DELEGATE;
        this._bindLighting = EMPTY_BIND_DELEGATE;
        this._bindMaterial = EMPTY_BIND_DELEGATE;
        // #endregion
        // #region implementation
        this.set = (pass) => {
            throw new Error('Deprecated.');
        };
        this.bind = (pass, ...buffers) => {
            throw new Error('Deprecated.');
        };
        this.draw = (pass, ...buffers) => {
            throw new Error('Deprecated.');
        };
        this.frame = (pass, interactionBuffer, meshBuffer, cameraBuffer, ...lightBuffers) => {
            // set
            pass.setPipeline(this._pipeline);
            try {
                // bind
                this._bindVertex(pass, meshBuffer);
                this._bindWorldTransform(pass, meshBuffer);
                this._bindMaterial(pass, meshBuffer);
                this._bindCameraProjectionAndInteractionData(pass, cameraBuffer, interactionBuffer);
                this._bindLighting(pass, ...lightBuffers);
                // draw
                const mesh = meshBuffer.item, geometry = mesh.geometry;
                pass.draw(geometry.triangleIndices?.length || geometry.positions.length);
            }
            catch (e) {
                console.error('Pacem.Drawing3D.FrameException', e);
            }
        };
        this.raycast = (pass, interactionBuffer, meshBuffer, cameraBuffer) => {
            // set
            pass.setPipeline(this._pickPipeline);
            try {
                // bind
                this._bindVertex(pass, meshBuffer);
                this._bindRaycastWorldTransform(pass, meshBuffer);
                this._bindRaycastCameraProjectionAndInteractionData(pass, cameraBuffer, interactionBuffer);
                this._bindPicker(pass, meshBuffer);
                // draw
                const mesh = meshBuffer.item, geometry = mesh.geometry;
                pass.draw(geometry.triangleIndices.length);
            }
            catch (e) { }
        };
        this._key = _builder.key;
    }
    get context() {
        return this._context;
    }
    get key() {
        return this._key;
    }
    build(pipelineOptions) {
        const options = Utils.extend({}, DefaultPipelineOptions, pipelineOptions ?? {});
        const builder = this._builder, wgsl = builder.build(options.modifiers);
        const ctx = Utils.extend({ pipeline: this }, this._context);
        const device = ctx.device;
        // #region VERTEX
        // build vertex state
        const vertex = {
            module: device.createShaderModule({ code: wgsl.vertex }),
            buffers: options.packedVertices ?
                [
                    // packed
                    BufferLayouts.createVertex(VERTEX_BUFFER_INDEX_MESH_POSITION, 3, 3, 2),
                ]
                : [
                    // position
                    BufferLayouts.createVertex(VERTEX_BUFFER_INDEX_MESH_POSITION, 3),
                    // normal
                    BufferLayouts.createVertex(VERTEX_BUFFER_INDEX_MESH_NORMAL, 3),
                    // uv
                    BufferLayouts.createVertex(VERTEX_BUFFER_INDEX_MESH_UV, 2)
                ]
        };
        // bind group layouts
        const viewBindGroupLayout = device.createBindGroupLayout({
            entries: [
                // camera matrices
                {
                    binding: UNIFORM_BINDING_INDEX_CAMERA,
                    visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
                    buffer: {
                        type: 'uniform'
                    }
                },
                // interaction
                {
                    binding: UNIFORM_BINDING_INDEX_INTERACTION,
                    visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
                    buffer: {
                        type: 'uniform'
                    }
                }
            ], label: labelPrefix + 'mesh-view-bind-group-layout'
        });
        const worldBindGroupLayout = device.createBindGroupLayout({
            entries: [
                {
                    // world- and normalMatrix entry
                    binding: UNIFORM_BINDING_INDEX_TRANSFORM,
                    visibility: GPUShaderStage.VERTEX,
                    buffer: {
                        type: 'uniform'
                    }
                }
            ], label: labelPrefix + 'mesh-transforms-bind-group-layout'
        });
        const bindGroupLayouts = [viewBindGroupLayout, worldBindGroupLayout];
        // #endregion
        // #region FRAGMENT
        // TODO: https://stackoverflow.com/questions/50720534/implementing-depth-testing-for-semi-transparent-objects
        // Weighted Blended Order-Independent Transparency https://jcgt.org/published/0002/02/09/
        // a-buffer: https://webgpu.github.io/webgpu-samples/?sample=a-buffer#composite.wgsl
        const fragment = {
            module: device.createShaderModule({
                code: wgsl.fragment
            }), targets: [
                {
                    format: ctx.format,
                    blend: {
                        color: {
                            srcFactor: 'src-alpha',
                            dstFactor: 'one-minus-src-alpha'
                        },
                        alpha: {},
                    }
                }
            ]
        };
        // material
        // material / material
        const materialGroupIndex = bindGroupLayouts.length;
        const materialBindGroupLayoutDescriptorEntries = [{
                // vertex colors entry
                binding: STORAGE_MATERIAL_INDEX,
                visibility: GPUShaderStage.FRAGMENT,
                buffer: {
                    type: 'read-only-storage'
                }
            }];
        // material / texture
        const { mapCount } = wgsl.metadata, hasTex = mapCount > 0;
        if (hasTex) {
            materialBindGroupLayoutDescriptorEntries.push({ visibility: GPUShaderStage.FRAGMENT, binding: SAMPLER_BINDING_INDEX, sampler: {} }, ...Array(mapCount).keys().map(index => { return { visibility: GPUShaderStage.FRAGMENT, binding: SAMPLER_BINDING_INDEX + 1 + index, texture: {} }; }));
        }
        else {
        }
        const materialBindGroupLayoutDescriptor = {
            label: labelPrefix + 'material-bind-group-layout',
            entries: materialBindGroupLayoutDescriptorEntries
        };
        const materialBindGroupLayout = device.createBindGroupLayout(materialBindGroupLayoutDescriptor);
        bindGroupLayouts.push(materialBindGroupLayout);
        // lighting
        const lightingGroupIndex = bindGroupLayouts.length;
        const { lightCount } = wgsl.metadata, hasLighting = lightCount > 0;
        if (hasLighting) {
            const lightingBindGroupLayout = device.createBindGroupLayout({
                entries: [...Array(lightCount).keys()].map((_, index) => {
                    return {
                        visibility: GPUShaderStage.FRAGMENT, binding: index, buffer: {
                            type: 'uniform'
                        }
                    };
                }),
                label: labelPrefix + 'lighting-bind-group-layout'
            });
            bindGroupLayouts.push(lightingBindGroupLayout);
        }
        // #endregion
        // #region PIPELINE
        // full pipeline descriptor
        const descriptor = {
            label: labelPrefix + 'mesh-render-pipeline',
            vertex,
            fragment,
            // pipeline-layout
            layout: device.createPipelineLayout({
                label: labelPrefix + 'mesh-render-pipeline-layout',
                bindGroupLayouts: (this._bindGroupLayouts = bindGroupLayouts)
            }),
            primitive: options.primitive
        };
        if (options.multisample) {
            descriptor.multisample = { count: 4 };
            descriptor.depthStencil = {
                depthCompare: 'less',
                format: 'depth24plus',
                depthWriteEnabled: true
            };
        }
        this._pipeline = device.createRenderPipeline(descriptor);
        // raycast pipeline descriptor
        const pickFragGroupLayout = this._pickBindGroupLayout = device.createBindGroupLayout({
            label: labelPrefix + 'mesh-colorpick-bind-group-layout',
            entries: [
                {
                    visibility: GPUShaderStage.FRAGMENT,
                    binding: 0,
                    buffer: { type: 'uniform' }
                }
            ]
        });
        const pickDescriptor = {
            label: labelPrefix + 'mesh-colorpick-pipeline',
            vertex,
            fragment: {
                module: device.createShaderModule({
                    code: getColorPickingFragmentWgsl(UNIFORM_GROUP_INDEX_COLORPICKING)
                }), targets: [
                    { format: 'r32uint' }
                ]
            },
            layout: device.createPipelineLayout({
                label: labelPrefix + 'mesh-colorpick-pipeline-layout',
                bindGroupLayouts: [viewBindGroupLayout, worldBindGroupLayout, pickFragGroupLayout]
            }),
            primitive: options.primitive,
            depthStencil: {
                depthWriteEnabled: true,
                depthCompare: 'less',
                format: 'depth24plus',
            },
        };
        this._pickPipeline = device.createRenderPipeline(pickDescriptor);
        // #endregion
        // vertex buffers
        const bindVertexSplit = (pass, ...buffers) => {
            const meshBuffer = buffers.at(0);
            if (Utils.isNull(meshBuffer)) {
                throw new ReferenceError(`Null ${RenderableBuffers.name} bringing vertex buffers was provided.`);
            }
            const { buffer: positions } = meshBuffer.buffer(ctx, 'geometryPositions');
            const { buffer: normals } = meshBuffer.buffer(ctx, 'geometryNormals');
            const { buffer: uv } = meshBuffer.buffer(ctx, 'geometryTexCoords');
            pass.setVertexBuffer(VERTEX_BUFFER_INDEX_MESH_POSITION, positions);
            pass.setVertexBuffer(VERTEX_BUFFER_INDEX_MESH_NORMAL, normals);
            pass.setVertexBuffer(VERTEX_BUFFER_INDEX_MESH_UV, uv);
        };
        const bindVertexPacked = (pass, ...buffers) => {
            const meshBuffer = buffers.at(0);
            if (Utils.isNull(meshBuffer)) {
                throw new ReferenceError(`Null ${RenderableBuffers.name} bringing vertex buffers was provided.`);
            }
            const { buffer } = meshBuffer.buffer(ctx, 'geometry');
            pass.setVertexBuffer(VERTEX_BUFFER_INDEX_MESH_POSITION, buffer);
        };
        this._bindVertex = options.packedVertices ? bindVertexPacked : bindVertexSplit;
        // world transform
        const bindWorldTransform = (pass, meshBuffer, raycasting) => {
            if (Utils.isNull(meshBuffer)) {
                throw new ReferenceError(`Null ${RenderableBuffers.name} bringing world transform buffer was provided.`);
            }
            const worldTransform = meshBuffer.buffer(ctx, 'objectWorldTransform'), worldTransformBuffer = worldTransform.buffer;
            // writes only if buffer is fresh
            PipelineUtils.writeBufferConditionally(device, worldTransform);
            const { group } = wgsl.bindings(WGSLBindingType.Mesh, 'objectWorldTransform', 0);
            const bindGroup = this._ensureWorldTransformBindGroup(meshBuffer.item, worldTransformBuffer, worldTransform.fresh /*false*/ /* persist the same bindGroup, given the constant size */, group, raycasting);
            pass.setBindGroup(group, bindGroup);
        };
        this._bindWorldTransform = (pass, ...buffers) => {
            const meshBuffer = buffers.at(0);
            bindWorldTransform(pass, meshBuffer, false);
        };
        this._bindRaycastWorldTransform = (pass, ...buffers) => {
            const meshBuffer = buffers.at(0);
            bindWorldTransform(pass, meshBuffer, true);
        };
        // camera matrices and interaction data
        const bindCameraProjectionAndInteractionData = (pass, cameraBuffer, interactionBuffer, raycasting) => {
            if (Utils.isNull(cameraBuffer)) {
                throw new ReferenceError(`Null ${RenderableBuffers.name} bringing view projection buffers was provided.`);
            }
            // camera view
            const viewTransformBuffer = cameraBuffer.buffer(ctx, 'cameraViewTransform'), viewTransformBufferBuffer = viewTransformBuffer.buffer;
            // writes only if buffer is fresh
            PipelineUtils.writeBufferConditionally(device, viewTransformBuffer);
            // interaction
            if (Utils.isNull(interactionBuffer)) {
                throw new ReferenceError(`Null ${RenderableBuffers.name} bringing interaction buffers was provided.`);
            }
            const interactionDataBuffer = interactionBuffer.buffer(ctx, 'interaction'), interactionDataBufferBuffer = interactionDataBuffer.buffer;
            // writes only if buffer is fresh
            PipelineUtils.writeBufferConditionally(device, interactionDataBuffer);
            const { group } = wgsl.bindings(WGSLBindingType.Camera, 'cameraViewTransform', 0);
            const { group: interactionGroup } = wgsl.bindings(WGSLBindingType.Interaction, 'interaction', 0);
            if (group !== interactionGroup) {
                throw new ReferenceError(`Layout bind group mismatch: ${group} vs ${interactionGroup}.`);
            }
            const bindGroup = this._ensureCameraProjectionAndInteractionDataBindGroup(cameraBuffer.item, viewTransformBufferBuffer, interactionDataBufferBuffer, 
            // since fresh could mean also "camera switched" (e.g. from ortho to perspective and then back to ortho) there's risk
            // to target destroyed buffers, this refresh the bind group if either buffer is fresh.
            // Possibilities for fine-tuning in future.
            viewTransformBuffer.fresh || interactionDataBuffer.fresh, group, raycasting);
            pass.setBindGroup(group, bindGroup);
        };
        this._bindCameraProjectionAndInteractionData = (pass, ...buffers) => {
            const cameraBuffer = buffers.at(0);
            const interactionBuffer = buffers.at(1);
            bindCameraProjectionAndInteractionData(pass, cameraBuffer, interactionBuffer, false);
        };
        this._bindRaycastCameraProjectionAndInteractionData = (pass, ...buffers) => {
            const cameraBuffer = buffers.at(0);
            const interactionBuffer = buffers.at(1);
            bindCameraProjectionAndInteractionData(pass, cameraBuffer, interactionBuffer, true);
        };
        // vertexColors and textures
        this._bindMaterial = (pass, ...buffers) => {
            const meshBuffer = buffers.at(0);
            if (Utils.isNull(meshBuffer)) {
                throw new ReferenceError(`Null ${RenderableBuffers.name} bringing vertex colors was provided.`);
            }
            const vertexColorsBuffer = meshBuffer.buffer(ctx, 'material'), vertexColorsBufferBuffer = vertexColorsBuffer.buffer;
            // writes only if buffer is fresh
            PipelineUtils.writeBufferConditionally(device, vertexColorsBuffer);
            let tex = meshBuffer.texture(ctx);
            const texBmp = tex?.texture;
            const bindGroup = this._ensureMaterialBindGroup(meshBuffer.item, vertexColorsBufferBuffer, texBmp, vertexColorsBuffer.fresh, materialGroupIndex);
            pass.setBindGroup(materialGroupIndex, bindGroup);
            if (hasTex) {
                device.queue.copyExternalImageToTexture({ source: tex.source, flipY: true }, { texture: texBmp }, { width: texBmp.width, height: texBmp.height });
            }
        };
        // lights
        this._bindLighting = (pass, ...buffers) => {
            if (hasLighting) {
                const lightArray = Array.from(buffers);
                const lightBuffers = lightArray.map(l => { return { buffer: l.buffer(ctx, 'light'), id: l.item.key }; });
                const refresh = lightBuffers.some(b => b.buffer.fresh);
                const bindGroup = this._ensureLightingBindGroup(lightBuffers, refresh, lightingGroupIndex);
                for (let lightBuffer of lightBuffers) {
                    // writes only if buffer is fresh
                    PipelineUtils.writeBufferConditionally(device, lightBuffer.buffer);
                }
                pass.setBindGroup(lightingGroupIndex, bindGroup);
            }
        };
        // picker
        this._bindPicker = (pass, ...buffers) => {
            const meshBuffer = buffers.at(0);
            if (Utils.isNull(meshBuffer)) {
                throw new ReferenceError(`Null ${RenderableBuffers.name} bringing world transform buffer was provided.`);
            }
            const u32id = meshBuffer.buffer(ctx, 'u32id'), u32idBuffer = u32id.buffer;
            // writes only if buffer is fresh
            PipelineUtils.writeBufferConditionally(device, u32id);
            const group = UNIFORM_GROUP_INDEX_COLORPICKING;
            const refresh = u32id.fresh;
            const bindGroup = this._ensureU32idBindGroup(meshBuffer.item, u32idBuffer, refresh /* reuse u32id.fresh*/);
            pass.setBindGroup(group, bindGroup);
        };
    }
}
class LineWGSLBuilder {
    #line;
    #camera;
    #bindgroups = new Map();
    constructor(...objects) {
        this.add(...objects);
    }
    reset() {
        this.#line = null;
        this.#bindgroups.clear();
    }
    add(...objects) {
        for (let obj of objects ?? []) {
            if (isMesh(obj.item)) {
                if (Utils.isNull(this.#line)) {
                    this.#line = obj;
                }
                else {
                    throw new Error('Line already added.');
                }
            }
            else if (isCamera(obj.item)) {
                this.#camera ??= obj;
            }
            else if (isLight(obj.item)) {
                // ignore
            }
            else {
                throw new Error('Unknown object type.');
            }
        }
    }
    build() {
        const data = this._computeBasicData();
        const key = this.key, vertex = this.vertex(data), fragment = this.fragment(data), bindings = (...args) => LineWGSLBuilder.prototype.bindgroup.apply(this, args);
        return {
            vertex,
            fragment,
            key,
            bindings,
            metadata: { thickness: data.thickness, dashPattern: data.dashPattern }
        };
    }
    get key() {
        const fat = (this.#line?.item).material?.lineWidth > 1;
        return KnownShader.Line + (fat ? '-fat' : '');
    }
    bindgroup(type, key, index) {
        const set = this.#bindgroups;
        const arr = set.get(type);
        if (Utils.isNull(arr)) {
            return null;
        }
        if (Utils.isNullOrEmpty(key)) {
            return arr;
        }
        const filteredArr = arr.filter(i => i.key === key);
        if (isNaN(index)) {
            return filteredArr;
        }
        if (index >= 0 && index < arr.length) {
            return filteredArr[index];
        }
        return filteredArr;
    }
    vertex(data = this._computeBasicData()) {
        return data.thickness > 1 ? getFatLineVertexWgsl() : getDefaultLineVertexWgsl();
    }
    _computeBasicData() {
        const mesh = this.#line.item;
        const shader = Utils.isNull(mesh) ? KnownShader.Line : (mesh.material?.shader ?? KnownShader.Line);
        const material = mesh.material;
        const thickness = material?.lineWidth ?? 1;
        const dashPattern = material?.dashArray;
        return {
            shader,
            thickness, dashPattern,
        };
    }
    _computeBindGroupsAndReferences(data = this._computeBasicData()) {
        const bindgroups = this.#bindgroups;
        // build bindings
        // mesh + material
        const worldTransform = { key: 'objectWorldTransform', group: UNIFORM_GROUP_INDEX_TRANSFORM, binding: UNIFORM_BINDING_INDEX_TRANSFORM, type: 'buffer' };
        const material = { key: 'material', group: STORAGE_GROUP_INDEX_LINE + 1, binding: STORAGE_MATERIAL_INDEX, type: 'buffer' };
        const geometryPositions = { key: 'geometryPositions', group: STORAGE_GROUP_INDEX_LINE, binding: 0, type: 'buffer' };
        const line = { key: 'line', group: STORAGE_GROUP_INDEX_LINE, binding: 1, type: 'buffer' };
        bindgroups.set(WGSLBindingType.Mesh, [worldTransform, material, geometryPositions, line]);
        // camera
        bindgroups.set(WGSLBindingType.Camera, [
            { key: 'cameraViewTransform', group: UNIFORM_GROUP_INDEX_CAMERA, binding: UNIFORM_BINDING_INDEX_CAMERA, type: 'buffer' }
        ]);
    }
    fragment(data = this._computeBasicData()) {
        this._computeBindGroupsAndReferences(data);
        const extra = {
            definitions: [], bindings: [], modifiers: []
        };
        switch (data.shader) {
            case KnownShader.Line:
                return LineMaterial.fragment(extra);
        }
    }
}
class LineRenderPipelineClass {
    #context;
    constructor(context, _lineBuilder, _cache = new RenderPipelineCache()) {
        this._lineBuilder = _lineBuilder;
        this._cache = _cache;
        // #region fields and utils
        this._ensureLineU32idBindGroup = (item, buffer, refresh) => {
            const ctx = this.context;
            return this._cache.ensureBindGroup(ctx, item, [buffer], refresh, this._pickLineBindGroupLayout, labelPrefix + 'line-colorpick-bind-group');
        };
        this._ensureLineMaterialBindGroup = (item, buffer, refresh, layoutIndex = MATERIAL_GROUP_INDEX) => {
            const layout = this._bindLineGroupLayouts[layoutIndex];
            if (layout.label !== (labelPrefix + 'line-material-bind-group-layout')) {
                throw new Error('BindGroupLayout mismatch!');
            }
            const ctx = this.context;
            return this._cache.ensureBindGroup(ctx, item, [buffer], refresh, layout, labelPrefix + 'line-material-bind-group');
        };
        this._ensureLinePositionsBindGroup = (item, buffers, refresh, layoutIndex) => {
            const layout = this._bindLineGroupLayouts[layoutIndex];
            if (layout.label !== (labelPrefix + 'line-positions-bind-group-layout')) {
                throw new Error('BindGroupLayout mismatch!');
            }
            const ctx = this.context;
            return this._cache.ensureBindGroup(ctx, item, buffers, refresh, layout, labelPrefix + 'line-positions-bind-group');
        };
        this._ensureLineWorldTransformBindGroup = (item, buffer, refresh, layoutIndex = UNIFORM_GROUP_INDEX_TRANSFORM, raycasting = false) => {
            const layout = this._bindLineGroupLayouts[layoutIndex];
            if (layout.label !== (labelPrefix + 'line-transforms-bind-group-layout')) {
                throw new Error('BindGroupLayout mismatch!');
            }
            const key = item.key + '-line-' + (raycasting ? 1 : 0);
            const label = `${labelPrefix}${raycasting ? 'colorpick' : ''}-line-transforms-bind-group`;
            const ctx = this.context;
            return this._cache.ensureBindGroup(ctx, key, [buffer], refresh, layout, label);
        };
        this._ensureLineCameraProjectionBindGroup = (item, viewBuffer, refresh, layoutIndex = UNIFORM_GROUP_INDEX_CAMERA, raycasting = false) => {
            const layout = this._bindLineGroupLayouts[layoutIndex];
            const key = item.key + '-line-' + (raycasting ? 1 : 0);
            const label = `${labelPrefix}${raycasting ? 'colorpick' : ''}-line-view-bind-group`;
            const ctx = this.context;
            return this._cache.ensureBindGroup(ctx, key, [viewBuffer], refresh, layout, label);
        };
        this._bindLineGroupLayouts = [];
        this._bindLinePositions = EMPTY_BIND_DELEGATE;
        this._bindLinePicker = EMPTY_BIND_DELEGATE;
        this._bindLineWorldTransform = EMPTY_BIND_DELEGATE;
        this._bindLineRaycastWorldTransform = EMPTY_BIND_DELEGATE;
        this._bindLineCameraProjection = EMPTY_BIND_DELEGATE;
        this._bindLineRaycastCameraProjection = EMPTY_BIND_DELEGATE;
        this._bindLineMaterial = EMPTY_BIND_DELEGATE;
        this._drawLine = EMPTY_BIND_DELEGATE;
        // #endregion
        // #region implementation
        this.set = (pass) => {
            throw new Error('Deprecated.');
        };
        this.bind = (pass, ...buffers) => {
            throw new Error('Deprecated.');
        };
        this.draw = (pass, ...buffers) => {
            throw new Error('Deprecated.');
        };
        this.frame = (pass, meshBuffer, cameraBuffer) => {
            // set
            pass.setPipeline(this._linePipeline);
            try {
                // bind
                this._bindLineWorldTransform(pass, meshBuffer);
                this._bindLineCameraProjection(pass, cameraBuffer);
                this._bindLinePositions(pass, meshBuffer);
                this._bindLineMaterial(pass, meshBuffer);
                // draw
                this._drawLine(pass, meshBuffer);
            }
            catch (e) {
                console.error('Pacem.Drawing3D.FrameException', e);
            }
        };
        this.raycast = (pass, meshBuffer, cameraBuffer) => {
            // set
            pass.setPipeline(this._pickLinePipeline);
            try {
                // bind
                this._bindLineRaycastWorldTransform(pass, meshBuffer);
                this._bindLineRaycastCameraProjection(pass, cameraBuffer);
                this._bindLinePositions(pass, meshBuffer);
                this._bindLinePicker(pass, meshBuffer);
                // draw
                this._drawLine(pass, meshBuffer);
            }
            catch (e) { }
        };
        this.#context = context;
        this._lineKey = _lineBuilder.key;
    }
    get context() {
        return this.#context;
    }
    get key() {
        return this._lineKey;
    }
    build(pipelineOptions) {
        const options = Utils.extend({}, DefaultPipelineOptions, pipelineOptions ?? {});
        const builder = this._lineBuilder, wgsl = builder.build();
        const ctx = Utils.extend({ pipeline: this }, this.#context);
        const device = ctx.device;
        // TODO: switch topology based on this flag
        const fatLines = wgsl.metadata.thickness > 1;
        // #region VERTEX
        // build vertex state
        const vertex = {
            module: device.createShaderModule({ code: wgsl.vertex }),
            buffers: []
        };
        // bind group layouts
        const worldBindGroupLayout = device.createBindGroupLayout({
            entries: [
                {
                    // world- and normalMatrix entry
                    binding: UNIFORM_BINDING_INDEX_TRANSFORM,
                    visibility: GPUShaderStage.VERTEX,
                    buffer: {
                        type: 'uniform'
                    }
                }
            ], label: labelPrefix + 'line-transforms-bind-group-layout'
        });
        const viewBindGroupLayout = device.createBindGroupLayout({
            entries: [
                // camera matrices
                {
                    binding: UNIFORM_BINDING_INDEX_CAMERA,
                    visibility: GPUShaderStage.VERTEX,
                    buffer: {
                        type: 'uniform'
                    }
                }
            ], label: labelPrefix + 'line-view-bind-group-layout'
        });
        const lineEntries = [
            // positions array
            {
                binding: 0,
                visibility: GPUShaderStage.VERTEX,
                buffer: {
                    type: 'read-only-storage'
                }
            }
        ];
        if (fatLines) {
            lineEntries.push(
            // line properties (width, dash-array, lineCap, ...)
            {
                binding: 1,
                visibility: GPUShaderStage.VERTEX,
                buffer: {
                    type: 'uniform'
                }
            });
        }
        const lineBindGroupLayout = device.createBindGroupLayout({
            entries: lineEntries, label: labelPrefix + 'line-positions-bind-group-layout'
        });
        const vertexBindGroupLayouts = [worldBindGroupLayout, viewBindGroupLayout, lineBindGroupLayout];
        const raycastLayoutIndex = vertexBindGroupLayouts.length;
        const bindGroupLayouts = vertexBindGroupLayouts.slice();
        // #endregion
        // #region FRAGMENT
        const fragment = {
            module: device.createShaderModule({
                code: wgsl.fragment
            }), targets: [
                {
                    format: ctx.format,
                    blend: {
                        color: {
                            srcFactor: 'src-alpha',
                            dstFactor: 'one-minus-src-alpha'
                        },
                        alpha: {},
                    }
                }
            ]
        };
        // material
        // material / material
        const materialGroupIndex = bindGroupLayouts.length;
        const materialBindGroupLayoutDescriptorEntries = [{
                // vertex colors entry
                binding: STORAGE_MATERIAL_INDEX,
                visibility: GPUShaderStage.FRAGMENT,
                buffer: {
                    type: 'read-only-storage'
                }
            }];
        const materialBindGroupLayoutDescriptor = {
            label: labelPrefix + 'line-material-bind-group-layout',
            entries: materialBindGroupLayoutDescriptorEntries
        };
        const materialBindGroupLayout = device.createBindGroupLayout(materialBindGroupLayoutDescriptor);
        bindGroupLayouts.push(materialBindGroupLayout);
        // #endregion
        // #region PIPELINE
        const topology = fatLines ? options.primitive.topology : 'line-list';
        // full pipeline descriptor
        const descriptor = {
            label: labelPrefix + 'line-render-pipeline',
            vertex,
            fragment,
            // pipeline-layout
            layout: device.createPipelineLayout({
                label: labelPrefix + 'line-render-pipeline-layout',
                bindGroupLayouts: (this._bindLineGroupLayouts = bindGroupLayouts)
            }),
            primitive: Utils.extend({}, options.primitive, { topology }),
        };
        if (options.multisample) {
            descriptor.multisample = { count: 4 };
            descriptor.depthStencil = {
                depthCompare: 'less',
                format: 'depth24plus',
                depthWriteEnabled: true
            };
        }
        this._linePipeline = device.createRenderPipeline(descriptor);
        // raycast pipeline descriptor
        const pickFragGroupLayout = this._pickLineBindGroupLayout = device.createBindGroupLayout({
            label: labelPrefix + 'line-colorpick-bind-group-layout',
            entries: [
                {
                    visibility: GPUShaderStage.FRAGMENT,
                    binding: 0,
                    buffer: { type: 'uniform' }
                }
            ]
        });
        const pickDescriptor = {
            label: labelPrefix + 'line-colorpick-pipeline',
            vertex,
            fragment: {
                module: device.createShaderModule({
                    code: getColorPickingFragmentWgsl(raycastLayoutIndex)
                }), targets: [
                    { format: 'r32uint' }
                ]
            },
            layout: device.createPipelineLayout({
                label: labelPrefix + 'line-colorpick-pipeline-layout',
                bindGroupLayouts: vertexBindGroupLayouts.concat(pickFragGroupLayout)
            }),
            primitive: Utils.extend({}, options.primitive, { topology }),
            depthStencil: {
                depthWriteEnabled: true,
                depthCompare: 'less',
                format: 'depth24plus',
            },
        };
        this._pickLinePipeline = device.createRenderPipeline(pickDescriptor);
        // #endregion
        // world transform
        const bindLineWorldTransform = (pass, meshBuffer, raycasting) => {
            if (Utils.isNull(meshBuffer)) {
                throw new ReferenceError(`Null ${RenderableBuffers.name} bringing world transform buffer was provided.`);
            }
            const worldTransform = meshBuffer.buffer(ctx, 'objectWorldTransform'), worldTransformBuffer = worldTransform.buffer;
            // writes only if buffer is fresh
            PipelineUtils.writeBufferConditionally(device, worldTransform);
            const { group } = wgsl.bindings(WGSLBindingType.Mesh, 'objectWorldTransform', 0);
            const bindGroup = this._ensureLineWorldTransformBindGroup(meshBuffer.item, worldTransformBuffer, 
            /*worldTransform.fresh*/ false /* persist the same bindGroup, given the constant size */, group, raycasting);
            pass.setBindGroup(group, bindGroup);
        };
        this._bindLineWorldTransform = (pass, ...buffers) => {
            const meshBuffer = buffers.at(0);
            bindLineWorldTransform(pass, meshBuffer, false);
        };
        this._bindLineRaycastWorldTransform = (pass, ...buffers) => {
            const meshBuffer = buffers.at(0);
            bindLineWorldTransform(pass, meshBuffer, true);
        };
        // camera matrices
        const bindLineCameraProjection = (pass, cameraBuffer, raycasting) => {
            if (Utils.isNull(cameraBuffer)) {
                throw new ReferenceError(`Null ${RenderableBuffers.name} bringing view projection buffers was provided.`);
            }
            const viewTransformBuffer = cameraBuffer.buffer(ctx, 'cameraViewTransform'), viewTransformBufferBuffer = viewTransformBuffer.buffer;
            // writes only if buffer is fresh
            PipelineUtils.writeBufferConditionally(device, viewTransformBuffer);
            const { group } = wgsl.bindings(WGSLBindingType.Camera, 'cameraViewTransform', 0);
            const bindGroup = this._ensureLineCameraProjectionBindGroup(cameraBuffer.item, viewTransformBufferBuffer, 
            /*viewTransformBuffer.fresh*/ false, group, raycasting);
            pass.setBindGroup(group, bindGroup);
        };
        this._bindLineCameraProjection = (pass, ...buffers) => {
            const cameraBuffer = buffers.at(0);
            bindLineCameraProjection(pass, cameraBuffer, false);
        };
        this._bindLineRaycastCameraProjection = (pass, ...buffers) => {
            const cameraBuffer = buffers.at(0);
            bindLineCameraProjection(pass, cameraBuffer, true);
        };
        // positions storage
        this._bindLinePositions = (pass, ...buffers) => {
            const meshBuffer = buffers.at(0);
            if (Utils.isNull(meshBuffer)) {
                throw new ReferenceError(`Null ${RenderableBuffers.name} bringing vertex buffers was provided as index 0.`);
            }
            // storage buffer
            const positionsBuffer = /* should have a storage buffer here */ meshBuffer.buffer(ctx, 'geometryPositions'), positionsBufferBuffer = positionsBuffer.buffer;
            let refresh = positionsBuffer.fresh;
            const buffersInput = [positionsBufferBuffer];
            // writes only if buffer is fresh
            PipelineUtils.writeBufferConditionally(device, positionsBuffer);
            if (fatLines) {
                const lineBuffer = meshBuffer.buffer(ctx, 'line'), lineBufferBuffer = lineBuffer.buffer;
                refresh ||= lineBuffer.fresh;
                buffersInput.push(lineBufferBuffer);
                // writes only if buffer is fresh
                PipelineUtils.writeBufferConditionally(device, lineBuffer);
            }
            const { group: groupIndex } = wgsl.bindings(WGSLBindingType.Mesh, 'line' /* or 'geometryPositions' */, 0);
            const bindGroup = this._ensureLinePositionsBindGroup(meshBuffer.item, buffersInput, refresh, groupIndex);
            pass.setBindGroup(groupIndex, bindGroup);
        };
        // vertexColors
        this._bindLineMaterial = (pass, ...buffers) => {
            const meshBuffer = buffers.at(0);
            if (Utils.isNull(meshBuffer)) {
                throw new ReferenceError(`Null ${RenderableBuffers.name} bringing vertex colors was provided.`);
            }
            const vertexColorsBuffer = meshBuffer.buffer(ctx, 'material'), vertexColorsBufferBuffer = vertexColorsBuffer.buffer;
            // writes only if buffer is fresh
            PipelineUtils.writeBufferConditionally(device, vertexColorsBuffer);
            const bindGroup = this._ensureLineMaterialBindGroup(meshBuffer.item, vertexColorsBufferBuffer, vertexColorsBuffer.fresh, materialGroupIndex);
            pass.setBindGroup(materialGroupIndex, bindGroup);
        };
        // picker
        this._bindLinePicker = (pass, ...buffers) => {
            const meshBuffer = buffers.at(0);
            if (Utils.isNull(meshBuffer)) {
                throw new ReferenceError(`Null ${RenderableBuffers.name} bringing world transform buffer was provided.`);
            }
            const u32id = meshBuffer.buffer(ctx, 'u32id'), u32idBuffer = u32id.buffer, u32idArray = u32id.value;
            // writes only if buffer is fresh
            PipelineUtils.writeBufferConditionally(device, u32id);
            const bindGroup = this._ensureLineU32idBindGroup(meshBuffer.item, u32idBuffer, /* reuse */ false /*u32id.fresh*/);
            pass.setBindGroup(raycastLayoutIndex, bindGroup);
        };
        this._drawLine = (pass, ...buffers) => {
            const meshBuffer = buffers.at(0);
            const mesh = meshBuffer.item, geometry = mesh.geometry /* as NodeGeometry*/;
            if (fatLines) {
                pass.draw(6, geometry.positions.length - 1, 0, 0);
            }
            else {
                pass.draw(geometry.positions.length);
            }
        };
    }
}
// #endregion
